import { useEffect, useMemo, useState } from "react";

import { ReleaseHealthOverview } from "@common/models/Analytics.model";
import { isNativePlatform } from "@common/models/Platform.model";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import useScoringConfig, { ScoringConfigKeys } from "@main/pages/Release/consumers/useScoringConfig";
import { useAnalyticsDeps } from "@main/pages/Release/Release";
import { ThresholdRowModel } from "@main/pages/Release/views/MetricDetails/shared/MetricRow";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

export default function useReleaseHealthScoring(refetchReleaseStatus: () => void) {
  const { scoringConfig, scoringConfigLoading, scoringConfigError, saveScoringConfig, isSaving } = useScoringConfig(
    ScoringConfigKeys.release_health,
  );

  const [searchParams] = useSearchParams();
  const selectedVersion = searchParams.get("version") ?? "";
  const { selectedWorkspace, selectedApp } = useAppSelectionContext();
  const { analyticsService } = useAnalyticsDeps();

  async function getReleaseStatus(signal?: AbortSignal): Promise<ReleaseHealthOverview | undefined> {
    if (!selectedWorkspace || !selectedApp || !selectedVersion) return undefined;

    const { data } = await analyticsService.getReleaseStats(
      selectedWorkspace.id,
      selectedApp.id,
      selectedVersion,
      signal,
    );

    return data;
  }

  const { data: healthData } = useQuery(
    ["releaseStatus", { selectedApp, selectedVersion }],
    ({ signal }) => getReleaseStatus(signal),
    {
      enabled: Boolean(selectedVersion),
      refetchOnWindowFocus: false,
      cacheTime: Infinity,
    },
  );

  const thresholdDefs = useMemo(
    () => (isNativePlatform(selectedApp) ? NATIVE_THRESHOLDS : NON_NATIVE_THRESHOLDS),
    [selectedApp],
  );

  const [thresholds, setThresholds] = useState<number[]>(() => thresholdDefs.map(() => 0));

  useEffect(() => {
    const config = scoringConfig?.config;
    if (!config) return;

    setThresholds(
      thresholdDefs.map((t) => {
        const raw = config[t.key];
        return raw !== undefined ? Math.round((raw as number) * 100) : 0;
      }),
    );
  }, [scoringConfig, thresholdDefs]);

  const handleThresholdChange = (index: number, value: number) => {
    setThresholds((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const percentageSum = thresholds.reduce((prev, curr) => prev + curr, 0);
  const isError = percentageSum !== 100;

  const handleSave = () => {
    const payload: Record<string, number> = {};
    thresholdDefs.forEach((def, i) => {
      payload[def.key] = thresholds[i] / 100;
    });
    saveScoringConfig(payload, { onSuccess: refetchReleaseStatus });
  };

  const thresholdRows: ThresholdRowModel[] = thresholdDefs.map((def, i) => ({
    score: healthData ? def.getScore(healthData) ?? undefined : undefined,
    label: def.label,
    value: thresholds[i],
    error: getValidationError(thresholds[i]),
    onChange: (v: number) => handleThresholdChange(i, v),
    description: getDescription(def),
  }));

  return {
    thresholdRows,
    percentageSum,
    isError,
    scoringConfigLoading,
    scoringConfigError,
    handleSave,
    isSaving,
  };
}

interface ThresholdConfig {
  key: string;
  label: string;
  default: number;
  getScore: (health?: ReleaseHealthOverview) => number | null | undefined;
}

const NATIVE_THRESHOLDS: ThresholdConfig[] = [
  {
    key: "crash_free_sessions_weight",
    label: "Crash-free sessions",
    default: 25,
    getScore: (health) => health?.crash_free_sessions.score,
  },
  {
    key: "anr_free_weight",
    label: "App hang-free sessions",
    default: 15,
    getScore: (health) => health?.anr_free_sessions.score,
  },
  {
    key: "network_health_weight",
    label: "Endpoints health",
    default: 15,
    getScore: (health) => health?.network_health.score,
  },
  {
    key: "network_speed_weight",
    label: "Network traffic speed",
    default: 15,
    getScore: (health) => health?.network_speed.score,
  },
  {
    key: "coverage_weight",
    label: "Coverage",
    default: 15,
    getScore: (health) => health?.testing_coverage.score,
  },
  {
    key: "resolution_rate_weight",
    label: "Resolution rate",
    default: 10,
    getScore: (health) => health?.resolution_rate.score,
  },
  { key: "mttr_weight", label: "MTTR", default: 5, getScore: (health) => health?.mttr.score },
];

const NON_NATIVE_THRESHOLDS: ThresholdConfig[] = [
  {
    key: "crash_free_sessions_weight",
    label: "Crash-free sessions",
    default: 30,
    getScore: (health) => health?.crash_free_sessions.score,
  },
  {
    key: "network_health_weight",
    label: "Endpoints health",
    default: 20,
    getScore: (health) => health?.network_health.score,
  },
  {
    key: "network_speed_weight",
    label: "Network traffic speed",
    default: 15,
    getScore: (health) => health?.network_speed.score,
  },
  {
    key: "coverage_weight",
    label: "Coverage",
    default: 15,
    getScore: (health) => health?.testing_coverage.score,
  },
  {
    key: "resolution_rate_weight",
    label: "Resolution rate",
    default: 10,
    getScore: (health) => health?.resolution_rate.score,
  },
  { key: "mttr_weight", label: "MTTR", default: 10, getScore: (health) => health?.mttr.score },
];

const getDescription = (def: ThresholdConfig): string => `System default: ${def.default}%.`;

const getValidationError = (value: number): string | undefined => {
  if (value < 0 || value > 100) return "Must be between 0% and 100%";
  return undefined;
};
